import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Save, X, CreditCard, ShoppingCart, Truck, RefreshCw, Zap, Loader2, Users } from "lucide-react";
import { IconTextButton } from "../../components/ButtonComponents";
import { toast } from "react-toastify";
import { useFirmModulesStore } from "../../store/firmModulesStore";

interface FirmModulesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  firmId: number;
  firmName: string;
}

export const FirmModulesDialog: React.FC<FirmModulesDialogProps> = ({
  isOpen,
  onClose,
  firmId,
  firmName,
}) => {
  const [formData, setFormData] = useState<any>(null);
  const { getFirmModules, applyPackage, updatePermissions, loading } = useFirmModulesStore();

  useEffect(() => {
    if (isOpen && firmId) {
      loadModules();
    } else {
      setFormData(null); // Dialog kapandığında temizle
    }
  }, [isOpen, firmId]);

  const loadModules = async () => {
    const data = await getFirmModules(firmId);
    if (data) {
      setFormData({
        ...data,
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : null
      });
    } else {
      // API hata dönerse veya veri yoksa bile iskeleti göster
      setFormData({ firmId, maxUserCount: 1 });
    }
  };

  const handlePackageApply = async (packageName: string) => {
    const ok = await applyPackage(firmId, packageName, 12);
    if (ok) {
      toast.success(`${packageName} paketi başarıyla uygulandı.`);
      loadModules();
    }
  };

  const handleSubmit = async () => {
    const ok = await updatePermissions({ ...formData, firmId });
    if (ok) {
      toast.success("Modül yetkileri güncellendi.");
      onClose();
    }
  };

  const moduleGroups = [
    {
      title: "Temel Ticari Modüller",
      icon: <ShoppingCart size={16} />,
      items: [
        { id: "userModule", label: "Kullanıcı & Personel & Rol" },
        { id: "stockModule", label: "Stok & Varyant" },
        { id: "accountModule", label: "Cari Yönetimi" },
        { id: "orderModule", label: "Sipariş Yönetimi" },
        { id: "invoiceModule", label: "Manuel Fatura" },
      ]
    },
    {
      title: "Finans & Tahsilat",
      icon: <CreditCard size={16} />,
      items: [
        { id: "financeModule", label: "Kasa & Banka" },
        { id: "paymentModule", label: "Tahsilat Fişleri" },
        { id: "vposModule", label: "Sanal POS (Online)" },
      ]
    },
    {
      title: "Saha Satış & CRM",
      icon: <Users size={16} />,
      items: [
        { id: "crmModule", label: "CRM & Görüşmeler" },
        { id: "visitModule", label: "Rut & Ziyaret Planı" },
      ]
    },
    {
      title: "Lojistik & Depo",
      icon: <Truck size={16} />,
      items: [
        { id: "warehouseModule", label: "Depo & Raf Takibi" },
        { id: "shipmentModule", label: "Sevkiyat & Kargo" },
      ]
    },
    {
      title: "Entegrasyonlar & Sistem",
      icon: <RefreshCw size={16} />,
      items: [
        { id: "eInvoiceModule", label: "E-Fatura (NES)" },
        { id: "eCommerceModule", label: "Pazaryeri (Trendyol vb.)" },
        { id: "syncModule", label: "ERP Senkronizasyon" },
        { id: "systemModule", label: "Sistem Modülü" },
        { id: "isClientSyncEnabled", label: "Client Senkronizasyon" },
      ]
    }
  ];

  return (
    <Dialog
      header={`${firmName} - SaaS Modül Yetkileri`}
      visible={isOpen}
      onHide={onClose}
      className="w-full md:w-10/12 lg:w-9/12"
      modal
      // bg-white gibi sınıfları sildik, temanıza uyum sağlar
      footer={
        <div className="flex justify-end gap-2">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
          <IconTextButton
            onClick={handleSubmit}
            icon={Save}
            className="btn-new"
          >
            {loading ? "Kaydediliyor..." : "Yetkileri Kaydet"}
          </IconTextButton>
        </div>
      }
    >
      {!formData && loading ? (
        <div className="flex flex-col items-center justify-center p-10 gap-4">
          <Loader2 className="animate-spin opacity-50" size={48} />
          <span className="text-sm opacity-50">Modül verileri yükleniyor...</span>
        </div>
      ) : formData ? (
        <div className="space-y-6 p-2">
          {/* Hızlı Paket Seçimi */}
          <section className="p-4 border rounded-xl border-inherit">
            <h4 className="flex items-center gap-2 font-bold mb-3 text-sm opacity-80">
              <Zap size={18} /> Hızlı Paket Tanımla (12 Aylık)
            </h4>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handlePackageApply('BASIC')}
                className="px-4 py-2 border rounded-lg hover:opacity-70 transition-all text-xs font-bold border-inherit"
              >
                BASIC
              </button>
              <button
                type="button"
                onClick={() => handlePackageApply('PRO')}
                className="px-4 py-2 border rounded-lg hover:opacity-70 transition-all text-xs font-bold border-blue-500/50"
              >
                PRO
              </button>
              <button
                type="button"
                onClick={() => handlePackageApply('ENTERPRISE')}
                className="px-4 py-2 border rounded-lg hover:opacity-70 transition-all text-xs font-bold border-purple-500/50"
              >
                ENTERPRISE
              </button>
            </div>
          </section>

          {/* Lisans ve Kapasite Bilgileri */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 p-4 border rounded-xl border-inherit">
              <label className="text-[10px] font-bold uppercase opacity-60">Lisans Bitiş Tarihi</label>
              <Calendar
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.value })}
                dateFormat="dd/mm/yy"
                showIcon
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-2 p-4 border rounded-xl border-inherit">
              <label className="text-[10px] font-bold uppercase opacity-60">Max. Kullanıcı Sayısı</label>
              <InputNumber
                value={formData.maxUserCount}
                onValueChange={(e) => setFormData({ ...formData, maxUserCount: e.value })}
                showButtons
                min={1}
                className="w-full"
              />
            </div>
          </section>

          {/* Modül Listesi (Grid) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {moduleGroups.map((group, idx) => (
              <div key={idx} className="space-y-3 p-3 border rounded-xl border-inherit">
                <h5 className="flex items-center gap-2 font-bold text-xs border-b pb-2 opacity-80 border-inherit">
                  {group.icon} {group.title}
                </h5>
                <div className="space-y-2">
                  {group.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between hover:opacity-80 transition-all">
                      <label htmlFor={item.id} className="text-[11px] cursor-pointer">{item.label}</label>
                      <Checkbox
                        inputId={item.id}
                        checked={formData[item.id] || false}
                        onChange={(e) => setFormData({ ...formData, [item.id]: e.checked })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-4 text-center opacity-50">Veri bulunamadı.</div>
      )}
    </Dialog>
  );
};